import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, MapPin, Quote, Sparkles } from "lucide-react";
import { Reveal, Rise } from "./Reveal";
import content from "../content";

function SeamlessCarousel({ slides, hint }) {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const [stops, setStops] = useState(slides.length);

  const slideW = () => ref.current?.firstElementChild?.offsetWidth || 1;
  useEffect(() => {
    const measure = () => {
      const el = ref.current;
      if (el) setStops(Math.max(1, slides.length - Math.round(el.clientWidth / slideW()) + 1));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [slides.length]);

  const onScroll = () => setActive(Math.round(ref.current.scrollLeft / slideW()));
  const go = (i) => {
    const n = Math.max(0, Math.min(stops - 1, i));
    ref.current?.scrollTo({ left: n * slideW(), behavior: "smooth" });
  };

  return (
    <div className="relative" data-testid="mission-carousel">
      <div
        ref={ref}
        onScroll={onScroll}
        className="no-scrollbar flex overflow-x-auto snap-x snap-mandatory rounded-3xl border border-white/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]"
      >
        {slides.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt={s.alt}
            loading={i < 3 ? "eager" : "lazy"}
            draggable="false"
            className="snap-start shrink-0 w-[85%] sm:w-1/2 lg:w-1/3 aspect-[4/5] object-cover select-none"
            data-testid={`mission-slide-${i}`}
          />
        ))}
      </div>

      {[-1, 1].map((d) => {
        const Icon = d < 0 ? ChevronLeft : ChevronRight;
        const disabled = d < 0 ? active === 0 : active >= stops - 1;
        return (
          <button
            key={d}
            type="button"
            onClick={() => go(active + d)}
            disabled={disabled}
            aria-label={d < 0 ? "Foto anterior" : "Foto siguiente"}
            data-testid={d < 0 ? "mission-prev" : "mission-next"}
            className={`absolute top-1/2 -translate-y-1/2 ${d < 0 ? "left-3" : "right-3"} w-11 h-11 rounded-full bg-black/55 backdrop-blur-sm text-white flex items-center justify-center transition-colors duration-300 hover:bg-electric disabled:opacity-0 disabled:pointer-events-none cursor-pointer`}
          >
            <Icon size={20} />
          </button>
        );
      })}

      <div className="mt-5 flex items-center justify-center gap-4">
        <div className="flex items-center gap-2">
          {Array.from({ length: stops }, (_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => go(i)}
              aria-label={`Ver foto ${i + 1}`}
              data-testid={`mission-dot-${i}`}
              className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                i === active ? "w-7 bg-electric" : "w-2 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
        <span className="font-poppins text-[11px] tracking-[0.22em] uppercase text-ceramic/50">{hint}</span>
      </div>
    </div>
  );
}

export default function MissionRecapSection() {
  const m = content.missionRecap;
  if (!m || m.hidden) return null;

  return (
    <section id="nicaragua" className="relative w-full bg-ink py-24 md:py-32 overflow-hidden" data-testid="mission-recap-section">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(circle at 85% 15%, rgba(33,112,232,0.16), transparent 55%)" }}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-7xl mx-auto px-6">
        <Reveal className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-end mb-12 md:mb-16">
          <div>
            <Rise>
              <span className="inline-flex items-center gap-2 rounded-full border border-electric/30 bg-electric/10 px-4 py-1.5 mb-6">
                <Sparkles size={15} className="text-electric" />
                <span className="font-poppins text-[11px] tracking-[0.24em] uppercase text-electric font-semibold">
                  {m.eyebrow}
                </span>
              </span>
            </Rise>
            <Rise>
              <h2 className="display text-white text-[clamp(2.75rem,8vw,5.5rem)] leading-[0.9] mb-6" data-testid="mission-title">
                {m.title}
              </h2>
            </Rise>
            <Rise>
              <div className="flex flex-wrap gap-2">
                {m.places?.map((p) => (
                  <span key={p} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 font-poppins text-xs md:text-sm text-white">
                    <MapPin size={13} className="text-electric" /> {p}
                  </span>
                ))}
              </div>
            </Rise>
          </div>
          <div>
            {m.paragraphs.map((p, i) => (
              <Rise key={i}>
                <p className="font-poppins text-ceramic/85 text-base md:text-lg leading-relaxed mb-4" data-testid={`mission-text-${i}`}>
                  {p}
                </p>
              </Rise>
            ))}
          </div>
        </Reveal>

        <Reveal amount={0.1}>
          <Rise>
            <SeamlessCarousel slides={m.slides} hint={m.swipeHint} />
          </Rise>
        </Reveal>

        <Reveal className="mt-14 md:mt-20 max-w-3xl mx-auto text-center">
          <Rise>
            <Quote size={30} className="mx-auto mb-5 text-electric" />
            <p className="font-poppins text-ceramic/60 text-xs md:text-sm uppercase tracking-[0.22em] mb-3">{m.quoteIntro}</p>
            <blockquote className="display text-white text-2xl sm:text-3xl md:text-4xl leading-tight" data-testid="mission-quote">
              “{m.quote}”
            </blockquote>
            <cite className="block not-italic mt-5 font-poppins text-electric text-sm tracking-[0.2em] uppercase">{m.quoteRef}</cite>
          </Rise>
        </Reveal>
      </div>
    </section>
  );
}
